import styled from "@emotion/styled"
import { PLinkPure } from "@porsche-design-system/components-react"
import type { BreakpointValues } from "@porsche-design-system/components-react"
import { spacingFluidMd, spacingFluidXl } from "@porsche-design-system/components-react/emotion"
import type { ConditionAndServiceView } from "@slfinrtl/ui-bff-routes"
import { defineMessages, useIntl } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { conditionAndServiceFieldMessages, conditionAndServiceFormMessages, Grid, GridItem, List } from "src/components"
import type { GridItemSize } from "src/components"

const messages = defineMessages({
	approximatedReconditioningCosts: {
		defaultMessage: "Approximated reconditioning costs",
		id: "vehicleDetails.overview.history.list.approximatedReconditioningCosts",
	},
	exCustomerReplacementCar: {
		defaultMessage: "Ex Customer Replacement Car",
		id: "vehicleDetails.overview.history.list.exCustomerReplacementCar",
	},
	exDemoCar: {
		defaultMessage: "Ex Demo Car",
		id: "vehicleDetails.overview.history.list.exDemoCar",
	},
	fullServiceHistory: {
		defaultMessage: "Full service history",
		id: "vehicleDetails.overview.history.list.fullServiceHistory",
	},
	hasPreviousDamages: {
		defaultMessage: "Has previous damages",
		id: "vehicleDetails.overview.history.list.hasPreviousDamages",
	},
	title: {
		defaultMessage: "Condition & History",
		id: "vehicleDetails.overview.history.header",
	},
})

interface ConditionAndServiceProps {
	carPassEnabled?: boolean
	view: ConditionAndServiceView
}

const gridSize: BreakpointValues<GridItemSize> = { base: 12, m: 6 }
export const ConditionAndService = ({ carPassEnabled, view }: ConditionAndServiceProps) => {
	const { formatMessage } = useIntl()
	const {
		accidentDescription,
		accidentFree,
		approximatedReconditioningCosts,

		carPassUrlFR,
		carPassUrlNL,

		batteryCapacityDate,
		batteryCapacityMileage,
		batteryCapacityPercentage,

		exCustomerReplacementCar,
		exDemoCar,
		firstRegistration,
		formerRentalCar,
		fullServiceHistory,
		hasPreviousDamages,
		knownAccidents,
		latestMaintenanceDate,
		latestMaintenanceMileage,
		latestMaintenanceScope,
		latestMaintenanceWorkshop,
		mileage,
		nextGeneralInspectionDate,
		nonSmoker,

		actualReconditioningCosts,
		numberOfPreviousOwners,
		previousDamageAmount,
		previousDamageDescription,
		previousOwnerUsageHistory,
		reconditioned,
		renewGeneralInspectionOnSale,
	} = view

	return (
		<Grid
			$columnGap={{ base: spacingFluidMd, s: spacingFluidXl }}
			$rowGap={spacingFluidMd}
			data-testid="overview-condition-and-service_view"
		>
			<StyledGridItem size={gridSize}>
				<List header={formatMessage(conditionAndServiceFormMessages.generalInformationTitle)}>
					{firstRegistration && (
						<List.Item
							label={formatMessage(conditionAndServiceFieldMessages.firstRegistrationDateLabel)}
							value={firstRegistration}
						/>
					)}
					<List.Item label={formatMessage(conditionAndServiceFieldMessages.mileageLabel)} value={mileage} />
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.previousOwnersLabel)}
						value={numberOfPreviousOwners}
					/>
					{previousOwnerUsageHistory && (
						<List.Item
							label={formatMessage(conditionAndServiceFieldMessages.previousOwnerUsageHistoryLabel)}
							value={previousOwnerUsageHistory}
						/>
					)}
					{carPassEnabled && (
						<List.Item
							label={formatMessage(conditionAndServiceFormMessages.carPassTitle)}
							value={
								carPassUrlFR || carPassUrlNL ? (
									<CarPassUrl carPassUrlFR={carPassUrlFR} carPassUrlNL={carPassUrlNL} />
								) : (
									emptyPlaceholder
								)
							}
						/>
					)}
				</List>
				{(!!batteryCapacityPercentage || batteryCapacityDate || batteryCapacityMileage) && (
					<List header={formatMessage(conditionAndServiceFormMessages.batteryInformationTitle)}>
						{batteryCapacityPercentage && (
							<List.Item
								label={formatMessage(conditionAndServiceFieldMessages.batteryCapacityLabel)}
								value={batteryCapacityPercentage}
							/>
						)}
						{batteryCapacityDate && (
							<List.Item
								label={formatMessage(conditionAndServiceFieldMessages.batteryCapacityDateLabel)}
								value={batteryCapacityDate}
							/>
						)}
						{batteryCapacityMileage && (
							<List.Item
								label={formatMessage(conditionAndServiceFieldMessages.batteryCapacityMileageLabel)}
								value={batteryCapacityMileage}
							/>
						)}
					</List>
				)}

				<div>
					<List header={formatMessage(conditionAndServiceFormMessages.damageHistoryTitle)}>
						{accidentFree && (
							<List.Item
								label={formatMessage(conditionAndServiceFieldMessages.accidentFreeLabel)}
								value={accidentFree}
							/>
						)}
						{knownAccidents && (
							<List.Item
								label={formatMessage(conditionAndServiceFieldMessages.knownAccidentsLabel)}
								value={knownAccidents}
							/>
						)}
						{accidentDescription?.map((item, index) => (
							<List.Item key={index} label={item.label} value={item.description} />
						))}
					</List>
				</div>
				<List header={formatMessage(conditionAndServiceFormMessages.serviceHistoryTitle)}>
					<List.Item label={formatMessage(messages.fullServiceHistory)} value={fullServiceHistory} />
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.latestMaintenanceDateTitle)}
						value={latestMaintenanceDate}
					/>
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.latestMaintenanceScopeLabel)}
						value={latestMaintenanceScope}
					/>
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.latestMaintenanceMileageLabel)}
						value={latestMaintenanceMileage}
					/>
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.latestMaintenanceWorkshopLabel)}
						value={latestMaintenanceWorkshop}
					/>
				</List>
			</StyledGridItem>
			<StyledGridItem size={gridSize}>
				<List header={formatMessage(conditionAndServiceFormMessages.precontractualInformation)}>
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.newOnSaleLabel)}
						value={renewGeneralInspectionOnSale}
					/>
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.nextGeneralInspectionDateLabel)}
						value={nextGeneralInspectionDate}
					/>
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.nonSmokerLabel)}
						value={nonSmoker}
					/>
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.formerRentalCarLabel)}
						value={formerRentalCar}
					/>
					<List.Item
						label={formatMessage(messages.exCustomerReplacementCar)}
						value={exCustomerReplacementCar}
					/>
					<List.Item label={formatMessage(messages.exDemoCar)} value={exDemoCar} />
					<List.Item label={formatMessage(messages.hasPreviousDamages)} value={hasPreviousDamages} />
					{previousDamageAmount && (
						<List.Item
							label={formatMessage(conditionAndServiceFieldMessages.previousDamageAmountLabel)}
							value={previousDamageAmount}
						/>
					)}
					{previousDamageDescription && (
						<List.Item
							label={formatMessage(conditionAndServiceFieldMessages.previousDamageDescriptionLabel)}
							value={previousDamageDescription}
						/>
					)}
					<List.Item
						label={formatMessage(conditionAndServiceFieldMessages.reconditioningLabel)}
						value={reconditioned}
					/>
					{actualReconditioningCosts && (
						<List.Item
							label={formatMessage(conditionAndServiceFieldMessages.actualCostsLabel)}
							value={actualReconditioningCosts}
						/>
					)}
					{approximatedReconditioningCosts && (
						<List.Item
							label={formatMessage(messages.approximatedReconditioningCosts)}
							value={approximatedReconditioningCosts}
						/>
					)}
				</List>
			</StyledGridItem>
		</Grid>
	)
}

const CarPassUrl = ({ carPassUrlFR, carPassUrlNL }: { carPassUrlFR?: string; carPassUrlNL?: string }) => (
	<CarPassUrlWrapper>
		{carPassUrlFR && (
			<PLinkPure underline icon="external" alignLabel="start" target="_blank" href={carPassUrlFR}>
				Car-Pass (FR)
			</PLinkPure>
		)}
		{carPassUrlNL && (
			<PLinkPure underline icon="external" alignLabel="start" target="_blank" href={carPassUrlNL}>
				Car-Pass (NL)
			</PLinkPure>
		)}
	</CarPassUrlWrapper>
)

const CarPassUrlWrapper = styled.div`
	display: flex;
	gap: ${spacingFluidMd};
`

const StyledGridItem = styled(GridItem)`
	display: flex;
	flex-direction: column;
	gap: ${spacingFluidMd};
`
